import clsx from 'clsx';
import type { ButtonHTMLAttributes } from 'react';
import { ns, useT } from '@/i18n';
import { Icon } from '../Icon/Icon';
import styles from './Stepper.module.css';

/** One 38 px round − / + button (`.step`). */
export function StepButton({
  direction,
  label,
  size = 38,
  className,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { direction: 'down' | 'up'; label: string; size?: 38 | 34 }) {
  return (
    <button
      type="button"
      aria-label={label}
      className={clsx(styles.step, size === 34 && styles.s34, className)}
      {...rest}
    >
      <Icon name={direction === 'down' ? 'minus' : 'plus'} size={14} strokeWidth={2.4} />
    </button>
  );
}

export interface NumberStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Accessible name of the control, e.g. «Adultes». Buttons read «Retirer : Adultes» / «Ajouter : Adultes». */
  label: string;
}

/** − value + with min/max (`.ppl .ctl`). */
export function NumberStepper({ value, onChange, min = 0, max = 99, label }: NumberStepperProps) {
  const t = useT(ns.common);
  return (
    <div className={styles.ctl} role="group" aria-label={label}>
      <StepButton
        direction="down"
        label={t('stepper.remove', { label })}
        disabled={value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      />
      <b aria-live="polite" aria-atomic="true">
        {value}
      </b>
      <StepButton
        direction="up"
        label={t('stepper.add', { label })}
        disabled={value >= max}
        onClick={() => onChange(Math.min(max, value + 1))}
      />
    </div>
  );
}
